import {
  isApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { ContentContainer } from '@components/ContentContainer';
import { ModalContent } from '@components/ModalContent';
import { Header } from '@components/ModalHeader';
import {
  FINTOC_TOKEN,
  GRAPHQL_URI,
  MEKI_FINTOC_WEEBHOOK,
  NEXT_PUBLIC_NO_PRICE_FALLBACK,
  isSafari,
} from '@config/environment';
import { getFintoc } from '@fintoc/fintoc-js';
import { useAuth } from '@hooks/useAuth';
import { useFacebokPixelEvent } from '@hooks/useFacebookPixel';
import { useMekiState } from '@hooks/useMekiState';
import { useModalRouter } from '@hooks/useModalRouter';
import { useOrderState } from '@hooks/useOrderState';
import { usePendingPrescriptions } from '@hooks/usePendingPrescriptions';
import { usePermissions } from '@hooks/usePermissions';
import { useSegment } from '@hooks/useSegment';
import { Order } from '@interfaces';
import {
  SeparateOrderDetailsByInsurance,
  SeparateOrderDetailsByInsuranceParams,
  SeparateOrderDetailsByInsuranceResponse,
} from '@queries/checkout/mutations/separateOrderByInsurance';
import {
  GetCheckoutDetailQueryParams,
  GetCheckoutDetailQueryResponse,
  OrderDeliveryOptions,
} from '@queries/checkout/queries/detail';
import {
  GetFinalSummaryQueryParams,
  GetFinalSummaryQueryResponse,
  getFinalSummary,
} from '@queries/checkout/queries/finalSummary';
import {
  GetCheckoutPaymentMethodQueryParams,
  GetCheckoutPaymentMethodQueryResponse,
  getCheckoutPaymentMethod,
} from '@queries/checkout/queries/paymentMethod';
import {
  FetchNextOrdersQueryResponse,
  getNextOrders,
} from '@queries/order/queries';
import {
  LastPurchasesQueryResponse,
  lastPurchasesQuery,
} from '@queries/order/queries/getLastOrdersPurchases';
import {
  OrdersQueryParams,
  PreviousOrdersQueryResponse,
  previousOrdersQuery,
} from '@queries/order/queries/previousOrders';
import {
  BancoEstadoPayment,
  BancoEstadoPaymentParams,
  BancoEstadoPaymentResponse,
  CancelBancoEstadoPayment,
  CancelBancoEstadoPaymentParams,
  CancelBancoEstadoPaymentResponse,
  CancelFintocPayment,
  CancelFintocPaymentParams,
  CancelFintocPaymentResponse,
  CancelPayOrderLocal,
  CancelPayOrderLocalMutationParams,
  CancelPayOrderLocalMutationResponse,
  ConfirmLocalPayment,
  ConfirmLocalPaymentMutationParams,
  ConfirmLocalPaymentMutationResponse,
  DeclarePayOrder,
  DeclarePayOrderMutationParams,
  DeclarePayOrderMutationResponse,
  PayOrder,
  PayOrderFintoc,
  PayOrderFintocMutationParams,
  PayOrderFintocMutationResponse,
  PayOrderInfo,
  PayOrderMutationParams,
  PayOrderMutationResponse,
  VerifyBancoEstadoJwt,
  VerifyBancoEstadoJwtParams,
  VerifyBancoEstadoJwtResponse,
} from '@queries/payment';
import {
  CurrentUserCreditsResponse,
  currentUserCredits,
} from '@queries/user/queries';
import { useOpenReplayTracking } from '@services/openreplay';
import { Console, getRoute, removeUndefineds } from '@utils';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import router, { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingScreen } from '../paymentMethod/components';
import { useTrackPayment } from '../paymentMethod/components/PayButton.hooks';
import { PendingPrescriptions } from '../paymentMethod/components/PendingPrescriptions';
import { useLocalPaymentOptionsActions } from '../paymentMethod/zustand/localPaymentOptionsStore';
import { getDeliveryCosts } from '../utils/getDeliveryCosts';
import { PayButton, Resume } from './components';
import { DeliveryDetails } from './components/DeliveryDetails';
import { ErrorModal, ErrorModalTypes } from './components/ErrorModal';
import { PaymentMethodDetails } from './components/PaymentMethodDetails';
import { ProductsList } from './components/ProductsList';
import {
  AddPadding,
  EmailNotificationTypography,
  ErrorNotificationTypography,
} from './styles';
import {
  LocalCancelButton,
  LocalTypographyButton,
} from './styles/PayButton.styles';
import { PayButtonContainer } from './styles/Resume.styles';
import { useCurrentOrderLoadingStore } from './zustand/currentOrderStore';
import { isMobile } from 'react-device-detect';

interface MetodoDePagoQuery extends ParsedUrlQuery {
  jwt?: string;
}

export function PaymentConfirm() {
  const nextRouter = useRouter();
  const modalRouter = useModalRouter();
  const {
    order: currentOrder,
    orderUuid,
    refetch: refetchOrder,
    loadingCurrentOrder,
  } = useOrderState();
  const { appUser: user } = useAuth();

  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const isAssistantUser = userCan('MANAGE_USER');

  const { track } = useSegment();
  const { trackIssue } = useOpenReplayTracking();
  const trackPayment = useTrackPayment();
  const { trackFBEvent } = useFacebokPixelEvent();

  const [payLoading, setPayLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [registryError, setRegistryError] = useState(true);
  const [prescriptionsError, setPrescriptionsError] = useState(false);
  const [isFintocSuccess, setIsFintocSucess] = useState<boolean>(false);
  const [fintocError, setFintocError] = useState(false);
  const [showCancelButton, setShowCancelButton] = useState<boolean>(false);
  const [notAllowedItemsError, setNotAllowedItemsError] =
    useState<boolean>(false);
  const [insuranceValidationError, setInsuranceValidationError] =
    useState(false);
  const closeFintocIntervalRef = useRef<NodeJS.Timer>();
  const { setInsuranceId, setInsuranceRut } = useMekiState();

  const { setLoadingCurrentOrder } = useCurrentOrderLoadingStore();
  const {
    requirePrescriptions,
    requirePrescriptionUpload,
    pendingPrescriptions,
    associatedPrescriptions,
  } = usePendingPrescriptions();

  const { data } = useQuery<
    GetCheckoutPaymentMethodQueryResponse,
    GetCheckoutPaymentMethodQueryParams
  >(getCheckoutPaymentMethod(), { variables: { params: { orderUuid } } });

  const { data: { orderDeliveryOptions } = {} } = useQuery<
    GetCheckoutDetailQueryResponse,
    GetCheckoutDetailQueryParams
  >(OrderDeliveryOptions(), {
    variables: { params: { orderUuid } },
    fetchPolicy: isLocalVendor ? 'cache-only' : 'cache-and-network',
  });
  const deliveryOption = orderDeliveryOptions?.find(
    (item) => item.dates.length === (currentOrder?.orderGroupId ? 2 : 1),
  );
  const ordersByDelivery = deliveryOption?.orders;

  const [orders, setOrders] = useState<Order[]>(ordersByDelivery);

  const [separateDetailsByInsurance] = useMutation<
    SeparateOrderDetailsByInsuranceResponse,
    SeparateOrderDetailsByInsuranceParams
  >(SeparateOrderDetailsByInsurance());

  const [payOrder] = useMutation<
    PayOrderMutationResponse,
    PayOrderMutationParams
  >(PayOrder('payOrderSummary'), {
    refetchQueries: [
      { query: getNextOrders() },
      {
        query: previousOrdersQuery('pedidos'),
        variables: { params: { pageNumber: 0 } },
      },
      {
        query: lastPurchasesQuery(),
      },
      {
        query: currentUserCredits(),
      },
    ],
  });
  const [payOrderFintoc] = useMutation<
    PayOrderFintocMutationResponse,
    PayOrderFintocMutationParams
  >(PayOrderFintoc());
  const [cancelFintocPayment] = useMutation<
    CancelFintocPaymentResponse,
    CancelFintocPaymentParams
  >(CancelFintocPayment());
  const [cancelPayOrderLocal] = useMutation<
    CancelPayOrderLocalMutationResponse,
    CancelPayOrderLocalMutationParams
  >(CancelPayOrderLocal());
  const [confirmLocalPayment] = useMutation<
    ConfirmLocalPaymentMutationResponse,
    ConfirmLocalPaymentMutationParams
  >(ConfirmLocalPayment());
  const [declarePayOrder] = useMutation<
    DeclarePayOrderMutationResponse,
    DeclarePayOrderMutationParams
  >(DeclarePayOrder());

  const [cancelBancoEstadoPayment] = useMutation<
    CancelBancoEstadoPaymentResponse,
    CancelBancoEstadoPaymentParams
  >(CancelBancoEstadoPayment());
  const [bancoEstadoPayment] = useMutation<
    BancoEstadoPaymentResponse,
    BancoEstadoPaymentParams
  >(BancoEstadoPayment());

  const [verifyBancoEstadoJwt] = useMutation<
    VerifyBancoEstadoJwtResponse,
    VerifyBancoEstadoJwtParams
  >(VerifyBancoEstadoJwt());

  const [prefetchOrder] = useLazyQuery<
    GetFinalSummaryQueryResponse,
    GetFinalSummaryQueryParams
  >(getFinalSummary());

  const [getNextOrdersQuery] = useLazyQuery<FetchNextOrdersQueryResponse>(
    getNextOrders(),
  );
  const [previousOrders] = useLazyQuery<
    PreviousOrdersQueryResponse,
    OrdersQueryParams
  >(previousOrdersQuery('pedidos'));
  const [lastPurchases] = useLazyQuery<LastPurchasesQueryResponse>(
    lastPurchasesQuery(),
  );
  const [currentUserCreditsQuery] = useLazyQuery<CurrentUserCreditsResponse>(
    currentUserCredits(),
  );

  const { currentUserInfo } = data ?? {};

  const orderMetadata = currentOrder?.metadata;
  const chargeDeliveryCost = orderMetadata?.chargeDeliveryCost ?? true;
  const useMekiCredits = orderMetadata?.useMekiCredits;
  const insuranceInfo = orderMetadata?.insuranceInfo;

  const ordersTotal = orders?.reduce((acc, order) => {
    const orderTotal = order.orderDetails.reduce((acc, detail) => {
      return acc + detail.quantity * detail.price;
    }, 0);

    if (!chargeDeliveryCost && isAssistantUser) {
      return acc + orderTotal;
    }

    return acc + orderTotal + order.deliveryCost;
  }, 0);

  const paymentMethod = currentOrder?.paymentMethod;
  const { deliveryCost, oldDeliveryCost } =
    (!chargeDeliveryCost && isAssistantUser) || !deliveryOption
      ? { deliveryCost: 0, oldDeliveryCost: 0 }
      : getDeliveryCosts(deliveryOption);

  // payAmount (to sent to payOrder) does not consider credits
  const payAmount = insuranceInfo?.insuranceId
    ? ordersTotal - (insuranceInfo?.totalReimbursement || 0)
    : ordersTotal;

  // Only to show on frontend
  const totalFinal =
    useMekiCredits && !isLocalVendor
      ? Math.max(ordersTotal - currentUserInfo?.credits, 0)
      : payAmount;

  const needPrescriptions =
    orders?.some((order) =>
      order.orderDetails.some((detail) =>
        ['NOT_REQUIRED', 'SIMPLE', 'HELD', 'CHECK'].includes(
          detail.product.prescriptionType,
        ),
      ),
    ) || insuranceInfo?.insuranceId;

  const ordersPayload = useMemo<PayOrderInfo[]>(() => {
    const usedStock: Record<number, number> = {};
    return ordersByDelivery
      ?.map((order: Order) => {
        const cleanReimbursedProducts =
          currentOrder?.metadata?.insuranceInfo?.reimbursedProducts?.map(
            (product) => ({
              sku: +product.sku,
              observation: product.observation,
              unitReimbursement: product.unitReimbursement,
              unitDeductible: product.unitDeductible,
              unitCopay: product.unitCopay,
              coverage: product.coverage,
              quantity: product.quantity,
            }),
          );

        return {
          deliveryDate: order.deliveryDate ?? '',
          reimbursedProductsInfo: cleanReimbursedProducts,
          details: order.orderDetails.map((detail) => {
            usedStock[detail.product.id] =
              (usedStock[detail.product.id] ?? 0) + detail.quantity;
            return {
              availability:
                usedStock[detail.product.id] > detail.product.availability.stock
                  ? detail.product.availability.statusWithoutStock ??
                    detail.product.availability.status
                  : detail.product.availability.status,
              price: detail.price ?? 0,
              productId: detail.product.id,
              quantity: detail.quantity,
            };
          }),
        };
      })
      .filter((e) => e.details.length > 0);
  }, [
    ordersByDelivery,
    currentOrder?.metadata?.insuranceInfo?.reimbursedProducts,
  ]);

  const handleHideError = () => {
    setError(false);
  };

  const handlePayment = useCallback(async () => {
    try {
      if (!orderDeliveryOptions) return;
      if (paymentMethod?.type == 'FINTOC') {
        const {
          data: {
            payOrderFintoc: { fintocToken, orderGroupId, uuid },
          },
        } = await payOrderFintoc({
          variables: {
            params: {
              orderUuid,
              orders: ordersPayload,
              amount: payAmount,
              useCredits: useMekiCredits,
              insuranceId: orderMetadata?.insuranceInfo?.insuranceId ?? null,
            },
          },
        }).catch((e: Error) => {
          setPayLoading(false);
          throw e;
        });
        const Fintoc = await getFintoc();
        const options = {
          widgetToken: fintocToken,
          holderType: 'individual',
          product: 'payments',
          publicKey: FINTOC_TOKEN,
          webhookUrl: GRAPHQL_URI + MEKI_FINTOC_WEEBHOOK,
          country: 'cl',
          onEvent: (event: string) => {
            Console.log('[FINTOC EVENT]: ', event);
            track(`fintoc event ${event}` as 'fintoc event', {
              userId: user?.id,
            });
          },
          onSuccess: () => {
            Console.error('fintoc success');
            void track('fintoc success', {
              userId: user?.id,
              orderId: currentOrder?.id,
            });
            setIsFintocSucess(true);
            setShowCancelButton(false);
            const interval = setInterval(() => {
              void prefetchOrder({
                variables: {
                  params: {
                    status: ['PAYMENT_CONFIRMED', 'DRAFT'],
                    uuid: orderGroupId ?? uuid,
                  },
                },
              })
                .then((response) => {
                  if (response.data) {
                    const status = response.data.ordersByUuid[0].status;
                    const cancelledByFintoc =
                      response.data.ordersByUuid[0]?.metadata
                        ?.cancelledByFintoc;
                    if (status === 'PAYMENT_CONFIRMED') {
                      clearInterval(interval);
                      clearInterval(closeFintocIntervalRef.current);
                      closeFintocIntervalRef.current = null;
                      const orderOrGroupUuid =
                        response.data.ordersByUuid[0].orderGroupId ??
                        response.data.ordersByUuid[0].uuid;
                      try {
                        trackPayment({
                          orders: orders,
                          orderId: currentOrder?.id,
                          totalPrice: ordersTotal,
                          deliveryPrice: deliveryCost,
                        });
                      } catch (e) {
                        Console.error('Error tracking payment');
                        Console.error(e);
                      }
                      try {
                        trackFBEvent(
                          'track',
                          'Purchase',
                          {
                            currency: 'CLP',
                            value: ordersTotal,
                          },
                          currentOrder?.uuid,
                        );
                      } catch (e) {
                        Console.error('Error tracking pixel payment');
                        Console.error(e);
                      }
                      void getNextOrdersQuery();
                      void previousOrders({
                        variables: { params: { pageNumber: 0 } },
                      });
                      void lastPurchases();
                      void currentUserCreditsQuery();
                      void router.push(
                        getRoute('checkoutPaymentSuccess', {
                          orderUuid: orderOrGroupUuid,
                        }),
                      );
                      setPayLoading(false);
                      return;
                    } else if (status === 'DRAFT' && cancelledByFintoc) {
                      clearInterval(interval);
                      if (closeFintocIntervalRef.current) {
                        clearInterval(closeFintocIntervalRef.current);
                        closeFintocIntervalRef.current = null;
                      }
                      setRegistryError(false);
                      setError(true);
                      setPayLoading(false);
                    }
                  }
                })
                .catch(() => {
                  Console.log('ORDER NOT CONRFIRMED BY FINTOC');
                });
            }, 2000);
            setInsuranceId(null);
            setInsuranceRut('');
            return;
          },
          onExit: async () => {
            setShowCancelButton(false);
            await cancelFintocPayment({
              variables: {
                params: {
                  orderUuid,
                },
              },
            }).catch((error) => {
              Console.error('fintoc cancel error');
              Console.error(error);
              void track('fintoc cancel error', {
                orderId: currentOrder?.id,
                userId: user?.id,
                error: error,
              });
            });
            Console.error('fintoc exit');
            void track('fintoc exit', {
              orderId: currentOrder?.id,
              userId: user?.id,
            });
            await refetchOrder();
            setRegistryError(false);
            setError(true);
            setFintocError(true);
            setPayLoading(false);
          },
        };
        const widget = Fintoc.create(options);
        widget.open();
        closeFintocIntervalRef.current = setInterval(() => {
          void prefetchOrder({
            variables: {
              params: {
                status: ['DRAFT'],
                uuid: orderGroupId ?? uuid,
              },
            },
          })
            .then((response) => {
              const fintocPaymentIntentId =
                response.data.ordersByUuid[0]?.metadata?.fintocPaymentIntentId;
              if (!fintocPaymentIntentId) {
                clearInterval(closeFintocIntervalRef.current);
                closeFintocIntervalRef.current = null;
                setRegistryError(false);
                setError(true);
                widget.destroy();
              }
            })
            .catch(() => {
              Console.log('ORDER NOT CANCELLED BY USER');
            });
        }, 2000);
        return;
      }
      if (paymentMethod?.type === 'BANCO_ESTADO') {
        const {
          data: {
            bancoEstadoPayment: { urlCompraqui },
          },
        } = await bancoEstadoPayment({
          variables: {
            params: {
              orderUuid,
              orders: ordersPayload,
              amount: payAmount,
              useCredits: useMekiCredits,
              insuranceId: orderMetadata?.insuranceInfo?.insuranceId ?? null,
            },
          },
        }).catch((e: Error) => {
          setPayLoading(false);
          throw e;
        });
        void router.push(urlCompraqui);
        await refetchOrder();
        setInsuranceId(null);
        setInsuranceRut('');
        return '';
      }
      const {
        data: {
          payOrder: { orderGroupId, uuid },
        },
      } = await payOrder({
        variables: {
          params: {
            orderUuid,
            orders: ordersPayload,
            amount: payAmount,
            useCredits: useMekiCredits,
            chargeDeliveryCost: chargeDeliveryCost ?? true,
            insuranceId: orderMetadata?.insuranceInfo?.insuranceId ?? null,
          },
        },
      }).catch((e: Error) => {
        const err = e;
        if (isApolloError(err)) {
          const isOrderWithNotAllowedProducts = err.graphQLErrors.some(
            (error) => {
              return error.message == '3005: Not all products are valid';
            },
          );
          if (isOrderWithNotAllowedProducts)
            setNotAllowedItemsError(isOrderWithNotAllowedProducts);
          if (err.message && err.message == 'Insurance info does not match') {
            setInsuranceValidationError(true);
          }
        }
        throw err;
      });
      try {
        trackPayment({
          orders: orders,
          orderId: currentOrder?.id,
          totalPrice: ordersTotal,
          deliveryPrice: deliveryCost,
        });
      } catch (e) {
        Console.error('Error tracking payment');
        Console.error(e);
      }
      try {
        trackFBEvent(
          'track',
          'Purchase',
          {
            currency: 'CLP',
            value: ordersTotal,
          },
          currentOrder?.uuid,
        );
      } catch (e) {
        Console.error('Error tracking pixel payment');
        Console.error(e);
      }
      await prefetchOrder({
        variables: {
          params: { status: ['PAYMENT_CONFIRMED'], uuid: orderGroupId ?? uuid },
        },
      }).finally(() => {
        setPayLoading(false);
      });

      const orderOrGroupUuid = orderGroupId ?? uuid;
      void router.push(
        getRoute('checkoutPaymentSuccess', {
          orderUuid: orderOrGroupUuid,
        }),
      );
      return '';
    } catch (e) {
      setPayLoading(false);
      Console.error('error', e);
      setRegistryError(false);
      setError(true);
    }
  }, [
    orderDeliveryOptions,
    paymentMethod?.type,
    payOrder,
    orderUuid,
    ordersPayload,
    payAmount,
    useMekiCredits,
    chargeDeliveryCost,
    orderMetadata?.insuranceInfo?.insuranceId,
    prefetchOrder,
    payOrderFintoc,
    track,
    user?.id,
    currentOrder?.id,
    currentOrder?.uuid,
    setInsuranceId,
    setInsuranceRut,
    getNextOrdersQuery,
    previousOrders,
    lastPurchases,
    currentUserCreditsQuery,
    trackPayment,
    orders,
    ordersTotal,
    deliveryCost,
    trackFBEvent,
    cancelFintocPayment,
    refetchOrder,
    bancoEstadoPayment,
  ]);

  const handlePaymentAction = useCallback(async () => {
    if (!paymentMethod) {
      return;
    }
    void track('order attempt', {
      paymentOptionSelected: paymentMethod?.type,
      payment_method_type: paymentMethod?.type,
      userId: user?.id,
      orderId: currentOrder?.id,
      chargeDeliveryCost,
    });
    await handlePayment();
  }, [
    paymentMethod,
    track,
    user?.id,
    currentOrder?.id,
    chargeDeliveryCost,
    handlePayment,
  ]);

  const handleCancel = useCallback(async () => {
    if (currentOrder?.paymentMethod.id === -6) {
      await cancelBancoEstadoPayment({
        variables: {
          params: {
            orderUuid,
          },
        },
      }).catch((error) => {
        Console.error('Error cancel banco estado payment');
        Console.error(error);
        void track('banco estado cancel error', {
          orderId: currentOrder?.id,
          userId: user?.id,
          error: error,
        });
        return;
      });
      Console.error('banco estado cancel');
      void track('banco estado cancel', {
        orderId: currentOrder?.id,
        userId: user?.id,
      });

      await refetchOrder();
      setError(false);
      setPayLoading(false);
      return;
    }
    await cancelFintocPayment({
      variables: {
        params: {
          orderUuid,
        },
      },
    }).catch((error) => {
      Console.error('Error cancel fintoc payment');
      Console.error(error);
      void track('fintoc cancel error', {
        orderId: currentOrder?.id,
        userId: user?.id,
        error: error,
      });
    });
    Console.error('fintoc cancel');
    void track('fintoc cancel', {
      orderId: currentOrder?.id,
      userId: user?.id,
    });

    await refetchOrder();
    setError(false);
    setPayLoading(false);
  }, [
    currentOrder?.paymentMethod?.id,
    currentOrder?.id,
    cancelFintocPayment,
    orderUuid,
    track,
    user?.id,
    refetchOrder,
    cancelBancoEstadoPayment,
  ]);

  const handleConfirmLocalPayment = useCallback(async () => {
    const {
      data: {
        confirmLocalPayment: { orderGroupId, uuid },
      },
    } = await confirmLocalPayment({
      variables: {
        params: {
          orderUuid,
        },
      },
    }).catch((e: Error) => {
      setPayLoading(false);
      throw e;
    });
    const orderOrGroupUuid = orderGroupId ?? uuid;
    try {
      trackPayment({
        orders: orders,
        orderId: currentOrder?.id,
        totalPrice: ordersTotal,
        deliveryPrice: deliveryCost,
      });
    } catch (e) {
      setPayLoading(false);
      Console.error('Error tracking payment');
      Console.error(e);
    }
    try {
      trackFBEvent(
        'track',
        'Purchase',
        {
          currency: 'CLP',
          value: ordersTotal,
        },
        currentOrder?.uuid,
      );
    } catch (e) {
      setPayLoading(false);
      Console.error('Error tracking pixel payment');
      Console.error(e);
    }
    void getNextOrdersQuery();
    void previousOrders({
      variables: { params: { pageNumber: 0 } },
    });
    void lastPurchases();
    void currentUserCreditsQuery();
    setPayLoading(false);
    void router.push(
      getRoute('checkoutPaymentSuccess', {
        orderUuid: orderOrGroupUuid,
      }),
    );
  }, [
    confirmLocalPayment,
    currentUserCreditsQuery,
    getNextOrdersQuery,
    lastPurchases,
    orders,
    orderUuid,
    previousOrders,
    trackPayment,
    trackFBEvent,
    ordersTotal,
    currentOrder,
    deliveryCost,
  ]);

  const { cleanValues: cleanValuesLocal } = useLocalPaymentOptionsActions();
  const handleCancelPayOrderLocal = useCallback(async () => {
    void cleanValuesLocal();
    await cancelPayOrderLocal({
      variables: {
        params: {
          orderUuid,
        },
      },
    })
      .catch((e: Error) => {
        void trackIssue(
          '[CANCEL_PAY_ORDER_LOCAL]: Error cancelling pay order local',
          {
            orderUuid,
          },
        );
        throw e;
      })
      .finally(() => {
        void router.push(getRoute('home'));
      });

    void router.push(getRoute('home'));
  }, [cancelPayOrderLocal, cleanValuesLocal, orderUuid, trackIssue]);

  const handleAssistantPaymentAction = useCallback(async () => {
    const paymentOption =
      currentOrder?.metadata?.assistantVendor?.paymentMethod;
    try {
      if (!paymentOption && !useMekiCredits) {
        setPayLoading(false);
        setErrorMessage('No se ha seleccionado un método de pago');
        return;
      }

      if (paymentOption === 'CARD') {
        await handlePaymentAction();
        return;
      }
      const {
        data: {
          declarePayOrder: { orderGroupId, uuid },
        },
      } = await declarePayOrder({
        variables: {
          params: {
            orderUuid,
            orders: ordersPayload,
            amount: payAmount,
            sendEmail: currentOrder?.metadata?.sendEmail ?? true,
            useCredits: useMekiCredits,
            paymentType: paymentOption,
            chargeDeliveryCost,
            insuranceId: orderMetadata?.insuranceInfo?.insuranceId,
          },
        },
      });

      await prefetchOrder({
        variables: {
          params: { status: ['PAYMENT_CONFIRMED'], uuid },
        },
      }).finally(() => {
        setPayLoading(false);
      });

      void router.push(
        getRoute('checkoutPaymentSuccess', {
          orderUuid: orderGroupId ?? uuid,
        }),
      );
    } catch (e) {
      setError(true);
      throw e;
    }
  }, [
    currentOrder?.metadata?.assistantVendor?.paymentMethod,
    currentOrder?.metadata?.sendEmail,
    useMekiCredits,
    declarePayOrder,
    orderUuid,
    ordersPayload,
    payAmount,
    chargeDeliveryCost,
    prefetchOrder,
    handlePaymentAction,
    orderMetadata?.insuranceInfo?.insuranceId,
  ]);

  const prescriptionComponentRef = useRef<HTMLDivElement>(null);
  const scrollToDiv = () => {
    if (prescriptionComponentRef.current) {
      prescriptionComponentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const prescriptionsValid = () => {
    const hasInsurance = Boolean(insuranceInfo?.claimId);
    const uploadedImages = pendingPrescriptions?.length > 0;
    if (hasInsurance && !uploadedImages && !associatedPrescriptions.length)
      return false;
    if (!requirePrescriptions) return true;
    if (!requirePrescriptionUpload) return true;
    return (
      pendingPrescriptions?.length > 0 || associatedPrescriptions.length > 0
    );
  };

  const handlePay = () => {
    setPayLoading(true);
    if (!prescriptionsValid()) {
      scrollToDiv();
      setPrescriptionsError(true);
      setPayLoading(false);
      return;
    }
    if (isLocalVendor) {
      void handleConfirmLocalPayment();
      return;
    }
    if (isAssistantUser) {
      void handleAssistantPaymentAction();
      return;
    }
    void handlePaymentAction();
    return;
  };

  const deliveryDatesAndProductsAmount =
    ordersPayload?.map((order) => {
      return { date: order.deliveryDate, productAmount: order.details.length };
    }) ?? null;

  const currencyFormatter = new CurrencyFormatter({
    fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
  });

  useEffect(() => {
    if (
      currentOrder?.status === 'PAYMENT_VALIDATION' &&
      !isLocalVendor &&
      !isAssistantUser
    ) {
      setPayLoading(true);
      setIsFintocSucess(true);
      setShowCancelButton(true);
    }
    if (currentOrder?.status === 'PAYMENT_VALIDATION' && isLocalVendor) {
      setPayLoading(false);
    }

    if (currentOrder?.status === 'PAYMENT_VALIDATION' && isAssistantUser) {
      setPayLoading(false);
      setShowCancelButton(true);
    }
  }, [currentOrder?.status, isAssistantUser, isLocalVendor]);

  useEffect(() => {
    const query: MetodoDePagoQuery = router.query;
    const { jwt } = query;
    if (jwt) {
      setPayLoading(true);
      void verifyBancoEstadoJwt({
        variables: {
          params: {
            jwt: jwt,
          },
        },
      })
        .then((response) => {
          const {
            uuid: orderUuid,
            orders,
            totalPrice,
            deliveryPrice,
          } = response?.data?.verifyBancoEstadoJwt;

          if (orderUuid) {
            void track('banco estado success', {
              userId: user?.id,
              orderId: currentOrder?.id,
            });
            try {
              trackPayment({
                orders,
                orderId: currentOrder?.id,
                totalPrice,
                deliveryPrice,
              });
            } catch (e) {
              Console.error('Error tracking payment');
              Console.error(e);
            }

            try {
              trackFBEvent(
                'track',
                'Purchase',
                {
                  currency: 'CLP',
                  value: ordersTotal,
                },
                currentOrder?.uuid,
              );
            } catch (e) {
              Console.error('Error tracking pixel payment');
              Console.error(e);
            }

            void getNextOrdersQuery();

            void previousOrders({
              variables: { params: { pageNumber: 0 } },
            });

            void lastPurchases();

            void currentUserCreditsQuery();

            void nextRouter.replace(
              {
                pathname: '/inicio',
                query: removeUndefineds({
                  ...nextRouter.query,
                  jwt: undefined,
                  pathname: `/checkout/pago-exitoso/[orderUuid]`,
                  orderUuid: orderUuid,
                }),
              },
              {
                pathname: `/checkout/pago-exitoso/${orderUuid}`,
                query: removeUndefineds({
                  ...router.query,
                  jwt: undefined,
                }),
              },
            );

            return;
          }
          void nextRouter.replace(
            {
              pathname: '/inicio',
              query: removeUndefineds({
                ...nextRouter.query,
                jwt: undefined,
                pathname: '/checkout/confirmar-pago',
              }),
            },
            {
              pathname: '/checkout/confirmar-pago',
              query: removeUndefineds({
                ...router.query,
                jwt: undefined,
              }),
            },
          );
          setPayLoading(false);
          setRegistryError(false);
          setError(true);
        })
        .catch(() => {
          void nextRouter.replace(
            {
              pathname: '/inicio',
              query: removeUndefineds({
                ...nextRouter.query,
                jwt: undefined,
                pathname: '/checkout/confirmar-pago',
              }),
            },
            {
              pathname: '/checkout/confirmar-pago',
              query: removeUndefineds({
                ...router.query,
                jwt: undefined,
              }),
            },
          );
          setPayLoading(false);
          setRegistryError(false);
          setError(true);
        });
    }
  }, [
    currentOrder?.id,
    currentOrder?.uuid,
    currentUserCreditsQuery,
    getNextOrdersQuery,
    lastPurchases,
    nextRouter,
    ordersTotal,
    previousOrders,
    track,
    trackFBEvent,
    trackPayment,
    user?.id,
    verifyBancoEstadoJwt,
  ]);

  useEffect(() => {
    setLoadingCurrentOrder(loadingCurrentOrder);
  }, [loadingCurrentOrder, setLoadingCurrentOrder]);

  const errorType = useMemo(() => {
    if (fintocError) return ErrorModalTypes.FINTOC;
    if (registryError) return ErrorModalTypes.REGISTRY;
    if (notAllowedItemsError) return ErrorModalTypes.NOT_ALLOWED_ITEMS;
    if (insuranceValidationError) return ErrorModalTypes.INVALID_INSURANCE;
    if (error) return ErrorModalTypes.DEFAULT;
    return ErrorModalTypes.NONE;
  }, [
    fintocError,
    registryError,
    notAllowedItemsError,
    insuranceValidationError,
    error,
  ]);

  useEffect(() => {
    const separateOrder = async () => {
      const cleanReimbursedProducts =
        currentOrder?.metadata?.insuranceInfo?.reimbursedProducts
          ?.filter((product) => product.coverage)
          .map((product) => ({
            sku: +product.sku,
            observation: product.observation,
            unitReimbursement: product.unitReimbursement,
            unitDeductible: product.unitDeductible,
            unitCopay: product.unitCopay,
            quantity: product.quantity,
            coverage: product.coverage,
          }));

      if (ordersByDelivery?.length && cleanReimbursedProducts?.length) {
        const response = await separateDetailsByInsurance({
          variables: {
            params: {
              orders: ordersByDelivery,
              reimbursedProducts: cleanReimbursedProducts,
            },
          },
        });
        setOrders(response.data?.separateOrderDetailsByInsurance);
      } else {
        setOrders(ordersByDelivery);
      }
    };
    void separateOrder();
  }, [ordersByDelivery, currentOrder, separateDetailsByInsurance]);

  return (
    <>
      <ContentContainer bottomNavigation={isMobile}>
        {payLoading ? (
          <LoadingScreen
            redirectMessage={
              paymentMethod?.type == 'FINTOC' ||
              paymentMethod?.type == 'BANCO_ESTADO'
            }
            isFintocSuccess={isFintocSuccess}
            showCancelButton={showCancelButton}
            handleCancel={handleCancel}
            isLocalVendor={isLocalVendor}
            isInsurance={paymentMethod?.type == 'INSURANCE'}
          />
        ) : (
          <>
            <Header
              title={'Confirmación de compra'}
              backIcon={!isLocalVendor}
              backFunction={() => {
                if (requirePrescriptions) {
                  void modalRouter.replace(getRoute('checkoutPrescriptions'));
                  return;
                }
                void modalRouter.replace(getRoute('checkoutPaymentMethod'));
              }}
              hideBorder
            />
            <ModalContent
              isSafari={isSafari}
              removePadding
              desktopHeight="calc(100vh - 150px)"
            >
              <Resume
                totalCost={ordersTotal}
                shippingCost={deliveryCost}
                oldShippingCost={oldDeliveryCost}
                totalFinal={totalFinal}
                handlePay={handlePay}
                paymentMethod={paymentMethod}
                handleCancelPayOrderLocal={handleCancelPayOrderLocal}
              />

              <AddPadding>
                {isAssistantUser && (
                  <>
                    {currentOrder?.metadata?.sendEmail ? (
                      <EmailNotificationTypography>
                        El cliente recibirá un correo{' '}
                      </EmailNotificationTypography>
                    ) : (
                      <EmailNotificationTypography>
                        El cliente no recibirá un correo
                      </EmailNotificationTypography>
                    )}
                  </>
                )}
                {isAssistantUser ||
                  (isLocalVendor && (
                    <>
                      {errorMessage.length > 0 && (
                        <ErrorNotificationTypography>
                          {errorMessage}
                        </ErrorNotificationTypography>
                      )}
                    </>
                  ))}

                <ProductsList
                  isDelivery={currentOrder?.deliveryType === 'DELIVERY'}
                  orders={orders}
                  insuranceInfo={insuranceInfo}
                />

                {!isLocalVendor && needPrescriptions && (
                  <div>
                    <PendingPrescriptions
                      title={'Recetas'}
                      hasInsurance={Boolean(insuranceInfo?.claimId)}
                      error={prescriptionsError}
                      setError={setPrescriptionsError}
                    />
                    <span ref={prescriptionComponentRef} />
                  </div>
                )}

                <DeliveryDetails
                  deliveryDatesAndProductsAmount={
                    deliveryDatesAndProductsAmount
                  }
                  address={currentOrder?.address}
                  deliveryType={currentOrder?.deliveryType}
                />

                <PaymentMethodDetails
                  paymentMethod={currentOrder?.paymentMethod}
                  creditsUsed={
                    useMekiCredits
                      ? currencyFormatter.format(
                          Math.min(currentUserInfo?.credits, ordersTotal),
                        )
                      : '$0'
                  }
                />

                <PayButtonContainer>
                  <PayButton
                    handleNext={handlePay}
                    text={
                      paymentMethod?.type == 'ONE_CLICK' ||
                      isLocalVendor ||
                      isAssistantUser ||
                      paymentMethod?.type == 'MEKI_CREDIT' ||
                      paymentMethod?.type == 'INSURANCE'
                        ? 'Confirmar compra'
                        : 'Ir a pagar'
                    }
                  />
                  {isLocalVendor && (
                    <LocalCancelButton onClick={handleCancelPayOrderLocal}>
                      <LocalTypographyButton>
                        Cancelar pago
                      </LocalTypographyButton>
                    </LocalCancelButton>
                  )}
                </PayButtonContainer>
              </AddPadding>
            </ModalContent>
          </>
        )}
      </ContentContainer>

      {error && (
        <ErrorModal
          handleHideError={handleHideError}
          uuid={orderUuid}
          errorType={errorType}
          refetchOrder={refetchOrder}
        />
      )}
    </>
  );
}
